import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { vedicService } from '../../services/api';
import SharedLocationForm from '../vedic/forms/LocationForm';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const TopGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:30px;
  @media(max-width:992px){grid-template-columns:1fr}
`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;

const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;



/* ─── Timing Cards ─── */
const TimingsGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px;margin-top:20px;
`;

const TimingCard = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:20px;
  border-left:4px solid ${p => p.color || p.theme.colors.primary};animation:${fadeIn} 0.3s ease;
`;

const TimingLabel = styled.div`font-size:0.8rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${p => p.color || p.theme.colors.text.secondary};`;
const TimingValue = styled.div`font-size:1.4rem;font-weight:700;color:${p => p.theme.colors.text.primary};margin:6px 0;`;
const TimingNote = styled.div`font-size:0.8rem;color:${p => p.theme.colors.text.muted};`;

/* ─── Choghadiya ─── */
const ChoghadiyaTable = styled.table`width:100%;border-collapse:collapse;font-size:0.85rem;margin-top:16px;`;
const ChogTh = styled.th`text-align:left;padding:10px;background:${p => p.theme.colors.background};border-bottom:2px solid ${p => p.theme.colors.border};font-weight:600;`;
const ChogTd = styled.td`padding:10px;border-bottom:1px solid ${p => p.theme.colors.border};`;
const ChogRow = styled.tr`
  background:${p => p.inauspicious ? '#fff5f5' : p.auspicious ? '#f1f8e9' : p.theme.colors.surface};
`;
const QualityBadge = styled.span`
  display:inline-block;padding:3px 10px;border-radius: ${p => p.theme.radius.lg}px;font-size:0.72rem;font-weight:700;
  background:${p => p.bg || (p.good ? '#e8f5e9' : '#fce4ec')};
  color:${p => p.fg || (p.good ? '#2e7d32' : p.theme.colors.error.text)};
`;

/* ─── Back Button ─── */
const BackButton = styled.button`
  background:none;border:1px solid ${p => p.theme.colors.border};border-radius:6px;padding:8px 20px;
  font-size:0.85rem;cursor:pointer;color:${p => p.theme.colors.text.secondary};margin-bottom:20px;
  &:hover{border-color:${p => p.theme.colors.primary};color:${p => p.theme.colors.primary}}
`;

const langLabels = { en: 'English', ta: 'Tamil' };

/* ─── Component ─── */
const DailyTimings = () => {
  const [lang, setLang] = useState('en');
  const [place, setPlace] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const chogTamilNames = { 'Udveg': 'உத்வேக்', 'Char': 'சர்', 'Labh': 'லாப்', 'Amrit': 'அமிர்த', 'Kaal': 'கால்', 'Shubh': 'சுப', 'Rog': 'ரோக்' };
  const planetTamil = { 'Sun': 'சூரியன்', 'Moon': 'சந்திரன்', 'Mars': 'செவ்வாய்', 'Mercury': 'புதன்', 'Jupiter': 'குரு', 'Venus': 'சுக்கிரன்', 'Saturn': 'சனி' };

  const handlePlaceChange = useCallback((data) => {
    setPlace(data.place);
    setLat(data.lat);
    setLon(data.lon);
    setTimezone(data.timezone);
  }, []);

  const fetchTimings = useCallback(async () => {
    if (!lat || !lon) {
      setError('Please select a place from the suggestions to get coordinates.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await vedicService.getDailyTimings({ lat, lon, date, tz: timezone, lang });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch daily timings');
    } finally {
      setLoading(false);
    }
  }, [lat, lon, date, timezone, lang]);

  const inauspiciousNames = ['rahu_kalam', 'rahu kalam', 'yamagandam', 'gulika_kalam', 'gulika kalam'];
  const isInauspicious = (name) => inauspiciousNames.some(n => (name || '').toLowerCase().includes(n));

  const auspiciousChoghadiya = ['amrit', 'shubh', 'labh', 'char'];
  const isAuspiciousChog = (name) => auspiciousChoghadiya.some(n => (name || '').toLowerCase().includes(n));

  return (
    <PageContainer>
      <PageTitle>{lang === 'ta' ? 'தினசரி நேரங்கள்' : 'Daily Timings'}</PageTitle>
      <PageSubtitle>{lang === 'ta' ? 'சூரிய உதயம், ராகு காலம், சோகடியா காலங்கள்' : 'Sunrise, Sunset, Rahu Kalam, Yamagandam, Gulika Kalam & Choghadiya (சோகடியா)'}</PageSubtitle>

      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto', padding: '6px 10px' }}>
          {Object.entries(langLabels).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </Select>
      </div>

      {!result ? (
        <TopGrid>
          <Card>
            <CardTitle>{lang === 'ta' ? 'இடம் & தேதி' : 'Location & Date'}</CardTitle>
            <SharedLocationForm
              placeValue={place}
              lat={lat}
              lon={lon}
              timezone={timezone}
              onPlaceChange={handlePlaceChange}
              showDate
              date={date}
              onDateChange={setDate}
              onSubmit={fetchTimings}
              submitLabel={lang === 'ta' ? 'நேரங்களைக் காட்டு' : 'Show Timings'}
              loading={loading}
              error={error}
              pageName="dailyTimings"
              lang={lang}
            />
          </Card>
          <Card>
            <CardTitle>{lang === 'ta' ? 'தினசரி நேரங்கள் என்றால் என்ன?' : 'What are Daily Timings?'}</CardTitle>
            <p style={{ color: '#555', lineHeight: 1.7, fontSize: '0.95rem' }}>
              {lang === 'ta'
                ? 'ராகு காலம், யமகண்டம், குளிகை காலம் ஆகியவை சுப நிகழ்வுகளைத் தொடங்குவதற்கு பொருத்தமற்ற நேரங்கள். சோகடியா முறை நாளின் ஒவ்வொரு பகுதிக்கும் சுப/அசுப தன்மையை வழங்குகிறது.'
                : 'Rahu Kalam, Yamagandam, and Gulika Kalam are inauspicious periods to avoid starting important activities. Choghadiya (சோகடியா) system divides the day into auspicious and inauspicious periods for planning activities.'}
            </p>
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ background: '#fce4ec', color: '#c62828', padding: '6px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>Inauspicious</span>
                <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '6px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>Auspicious</span>
              </div>
            </div>
          </Card>
        </TopGrid>
      ) : (
        <>
          <BackButton onClick={() => setResult(null)}>
            {lang === 'ta' ? 'புதிய தேடல்' : 'New Search'}
          </BackButton>

          {/* Sun timings */}
          <TimingsGrid>
            <TimingCard color="#ff9800">
              <TimingLabel color="#e65100">
                {lang === 'ta' ? 'சூரிய உதயம்' : 'Sunrise'}
              </TimingLabel>
              <TimingValue>{result.sunrise || result.sun?.sunrise || '--'}</TimingValue>
            </TimingCard>
            <TimingCard color="#ff9800">
              <TimingLabel color="#e65100">
                {lang === 'ta' ? 'சூரிய அஸ்தமனம்' : 'Sunset'}
              </TimingLabel>
              <TimingValue>{result.sunset || result.sun?.sunset || '--'}</TimingValue>
            </TimingCard>
          </TimingsGrid>

          {/* Inauspicious periods */}
          <h3 style={{ marginTop: 30, marginBottom: 10, color: '#c62828' }}>
            {lang === 'ta' ? 'அசுப காலங்கள்' : 'Inauspicious Periods'}
          </h3>
          <TimingsGrid>
            {(() => {
              const t = result.timings || result;
              const fmtTime = (iso) => { if (!iso) return '-'; try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }); } catch { return iso; } };
              const tamilNames = { 'Rahu Kalam': 'ராகு காலம்', 'Yamagandam': 'யமகண்டம்', 'Gulika Kalam': 'குளிகை காலம்' };
              return (result.inauspicious_periods || [
                { name: 'Rahu Kalam', start: t.rahu_kalam?.start, end: t.rahu_kalam?.end },
                { name: 'Yamagandam', start: t.yamagandam?.start, end: t.yamagandam?.end },
                { name: 'Gulika Kalam', start: t.gulika_kalam?.start, end: t.gulika_kalam?.end },
              ]).map((period, i) => (
                <TimingCard key={i} color="#f44336">
                  <TimingLabel color="#c62828">{lang === 'ta' ? (tamilNames[period.name] || period.name) : (period.name || 'Period')}</TimingLabel>
                  <TimingValue>{fmtTime(period.start)} - {fmtTime(period.end)}</TimingValue>
                  {period.duration && <TimingNote>Duration: {period.duration}</TimingNote>}
                </TimingCard>
              ));
            })()}
          </TimingsGrid>

          {/* Choghadiya */}
          {result.choghadiya && (
            <Card style={{ marginTop: 30 }}>
              <CardTitle>{lang === 'ta' ? 'சோகடியா காலங்கள்' : 'Choghadiya (சோகடியா) Periods'}</CardTitle>

              {/* Day Choghadiya */}
              {(result.choghadiya?.day || []).length > 0 && (
                <>
                  <h3 style={{ fontSize: '1rem', color: '#555', marginBottom: 8 }}>
                    {lang === 'ta' ? 'பகல்' : 'Day'}
                  </h3>
                  <ChoghadiyaTable>
                    <thead>
                      <tr>
                        <ChogTh>{lang === 'ta' ? 'பெயர்' : 'Name'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'தொடக்கம்' : 'Start'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'முடிவு' : 'End'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'தன்மை' : 'Quality'}</ChogTh>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.choghadiya?.day || []).map((c, i) => {
                        const good = isAuspiciousChog(c.name);
                        const bad = isInauspicious(c.name) || (c.quality && c.quality.toLowerCase() === 'inauspicious');
                        return (
                          <ChogRow key={i} auspicious={good} inauspicious={bad}>
                            <ChogTd><strong>{lang === 'ta' ? (chogTamilNames[c.name] || c.name) : c.name}</strong> ({lang === 'ta' ? (planetTamil[c.planet] || c.planet) : c.planet})</ChogTd>
                            <ChogTd>{c.display_start || c.start}</ChogTd>
                            <ChogTd>{c.display_end || c.end}</ChogTd>
                            <ChogTd>
                              <QualityBadge good={good} bg={c.color ? c.color + '20' : undefined} fg={c.color || undefined}>{good ? (lang === 'ta' ? 'சுபம்' : 'Auspicious') : (lang === 'ta' ? 'அசுபம்' : 'Inauspicious')}</QualityBadge>
                            </ChogTd>
                          </ChogRow>
                        );
                      })}
                    </tbody>
                  </ChoghadiyaTable>
                </>
              )}

              {/* Night Choghadiya */}
              {(result.choghadiya?.night || []).length > 0 && (
                <>
                  <h3 style={{ fontSize: '1rem', color: '#555', marginTop: 24, marginBottom: 8 }}>
                    {lang === 'ta' ? 'இரவு' : 'Night'}
                  </h3>
                  <ChoghadiyaTable>
                    <thead>
                      <tr>
                        <ChogTh>{lang === 'ta' ? 'பெயர்' : 'Name'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'தொடக்கம்' : 'Start'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'முடிவு' : 'End'}</ChogTh>
                        <ChogTh>{lang === 'ta' ? 'தன்மை' : 'Quality'}</ChogTh>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.choghadiya?.night || []).map((c, i) => {
                        const good = isAuspiciousChog(c.name);
                        const bad = isInauspicious(c.name) || (c.quality && c.quality.toLowerCase() === 'inauspicious');
                        return (
                          <ChogRow key={i} auspicious={good} inauspicious={bad}>
                            <ChogTd><strong>{lang === 'ta' ? (chogTamilNames[c.name] || c.name) : c.name}</strong> ({lang === 'ta' ? (planetTamil[c.planet] || c.planet) : c.planet})</ChogTd>
                            <ChogTd>{c.display_start || c.start}</ChogTd>
                            <ChogTd>{c.display_end || c.end}</ChogTd>
                            <ChogTd>
                              <QualityBadge good={good} bg={c.color ? c.color + '20' : undefined} fg={c.color || undefined}>{good ? (lang === 'ta' ? 'சுபம்' : 'Auspicious') : (lang === 'ta' ? 'அசுபம்' : 'Inauspicious')}</QualityBadge>
                            </ChogTd>
                          </ChogRow>
                        );
                      })}
                    </tbody>
                  </ChoghadiyaTable>
                </>
              )}

            </Card>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default DailyTimings;
